import { useState, useEffect } from 'react';
import { User, Camera, Mail, Phone, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { settingsService } from '../../services/settingsService.js';
import type { UserProfile, ProfileUpdateRequest } from '../../services/settingsService.js';
import { toast } from 'react-hot-toast';

const ProfileTab = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalProfile, setOriginalProfile] = useState<UserProfile | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const loadedProfile = await settingsService.loadProfile();
        setProfile(loadedProfile);
        setOriginalProfile(loadedProfile);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load profile';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const validateEmail = (email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      return null;
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    if (!profile) return;
    
    if (field === 'email') {
      const error = validateEmail(value);
      setEmailError(error);
    }
    
    const updatedProfile = { ...profile, [field]: value };
    setProfile(updatedProfile);
    
    if (originalProfile) {
      const hasChanges = Object.keys(updatedProfile).some(key => {
        const k = key as keyof UserProfile;
        return updatedProfile[k] !== originalProfile[k];
      });
      setHasChanges(hasChanges);
    }
  };

  const handleSave = async () => {
    if (!profile || !hasChanges) return;
    
    if (emailError) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    setSaving(true);
    try {
      const updates: ProfileUpdateRequest = {};
      
      if (originalProfile) {
        Object.keys(profile).forEach(key => {
          const k = key as keyof UserProfile;
          if (profile[k] !== originalProfile[k] && k !== 'id' && k !== 'createdAt' && k !== 'updatedAt') {
            (updates as Record<string, unknown>)[k] = profile[k];
          }
        });
      }

      const updatedProfile = await settingsService.updateProfile(updates);
      setProfile(updatedProfile);
      setOriginalProfile(updatedProfile);
      setHasChanges(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalProfile) {
      setProfile(originalProfile);
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Profile</h2>
          <p className="text-slate-400 mt-1">Manage your personal information and preferences</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-brand-accent" />
          <span className="ml-3 text-slate-400">Loading profile...</span>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Profile</h2>
          <p className="text-slate-400 mt-1">Manage your personal information and preferences</p>
        </div>
        <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error || 'Failed to load profile'}</p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => window.location.reload()}
            className="mt-3"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Profile</h2>
        <p className="text-slate-400 mt-1">Manage your personal information and preferences</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User size={20} className="text-brand-accent" />
            <span>Personal Information</span>
          </CardTitle>
          <CardDescription>Update your profile details and contact information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-brand-primary to-brand-accent rounded-full flex items-center justify-center">
                <User size={32} className="text-white" />
              </div>
              <Button
                size="sm"
                className="absolute -bottom-2 -right-2 rounded-full w-8 h-8 p-0"
                variant="secondary"
              >
                <Camera size={14} />
              </Button>
            </div>
            <div>
              <h3 className="text-lg font-semibold cream-gradient-text text-drop-shadow">{profile.firstName} {profile.lastName}</h3>
              <p className="text-slate-400">{profile.email}</p>
              <Button variant="ghost" size="sm" className="mt-2">
                Change Avatar
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">First Name</label>
              <Input
                data-devinid="profile-first-name-input"
                value={profile.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="violett-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
              <Input
                data-devinid="profile-last-name-input"
                value={profile.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="violett-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                data-devinid="profile-email-input"
                value={profile.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`violett-input pl-10 ${emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                type="email"
              />
            </div>
            {emailError && (
              <p className="text-red-400 text-sm mt-1">{emailError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                data-devinid="profile-phone-input"
                value={profile.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="violett-input pl-10"
                type="tel"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                data-devinid="profile-location-input"
                value={profile.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="violett-input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Bio</label>
            <textarea
              data-devinid="profile-bio-input"
              value={profile.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              className="violett-input min-h-[100px] resize-none"
              placeholder="Tell us about yourself..."
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button 
              data-devinid="profile-cancel-button"
              variant="ghost" 
              onClick={handleCancel}
              disabled={!hasChanges || saving}
            >
              Cancel
            </Button>
            <Button 
              data-devinid="profile-save-button"
              onClick={handleSave}
              disabled={!hasChanges || saving || !!emailError}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileTab;
